import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Star, CheckCircle, XCircle, Trash2 } from "lucide-react";
import ImageLightbox from "@/components/ImageLightbox";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const ReviewsModeration = ({ reviews, loading, token, onReviewsUpdate }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const handleApproveReview = async (reviewId) => {
    try {
      await axios.put(`${API}/reviews/${reviewId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update local state
      onReviewsUpdate(reviews.map(r => r.id === reviewId ? {...r, is_approved: true} : r));
    } catch (err) {
      console.error("Failed to approve review:", err);
      alert("Failed to approve review");
    }
  };

  const handleRejectReview = async (reviewId) => {
    try {
      await axios.put(`${API}/reviews/${reviewId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update local state
      onReviewsUpdate(reviews.map(r => r.id === reviewId ? {...r, is_approved: false} : r));
    } catch (err) {
      console.error("Failed to reject review:", err);
      alert("Failed to reject review");
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!confirm("Are you sure you want to delete this review?")) return;
    
    try {
      await axios.delete(`${API}/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Remove from local state
      onReviewsUpdate(reviews.filter(r => r.id !== reviewId));
    } catch (err) {
      console.error("Failed to delete review:", err);
      alert("Failed to delete review");
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-2xl">Review Moderation</CardTitle>
            <div className="flex gap-4">
              <div className="text-sm text-gray-600">
                <span className="font-semibold">{reviews.filter(r => !r.is_approved).length}</span> Pending
              </div>
              <div className="text-sm text-gray-600">
                <span className="font-semibold">{reviews.filter(r => r.is_approved).length}</span> Approved
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center py-8 text-gray-600">Loading reviews...</p>
          ) : reviews.length === 0 ? (
            <p className="text-center py-8 text-gray-600">No reviews yet</p>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <div
                  key={review.id}
                  className={`p-6 rounded-lg border-2 ${
                    review.is_approved 
                      ? 'border-green-200 bg-green-50' 
                      : 'border-yellow-200 bg-yellow-50'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-4">
                    <div className="min-w-0">
                      <h3 className="text-lg font-bold text-gray-900">{review.name}</h3>
                      <p className="text-sm text-gray-600 break-words">
                        {review.country} • {review.program} at {review.dive_center}, {review.country_dove}
                      </p>
                      <div className="flex gap-1 mt-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-4 h-4 ${
                              star <= review.rating
                                ? "fill-yellow-400 text-yellow-400"
                                : "text-gray-300"
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0">
                      {!review.is_approved && (
                        <Button
                          size="sm"
                          onClick={() => handleApproveReview(review.id)}
                          className="bg-green-500 hover:bg-green-600 text-white"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                      )}
                      {review.is_approved && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRejectReview(review.id)}
                          className="border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Unapprove
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteReview(review.id)}
                        className="bg-red-500 hover:bg-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{review.review_text}</p>
                  
                  {(() => {
                    const images = review.image_urls && review.image_urls.length > 0 
                      ? review.image_urls 
                      : review.image_url ? [review.image_url] : [];
                    
                    if (images.length === 0) return null;
                    
                    return (
                      <div className="mt-4">
                        <div className="flex gap-2 overflow-x-auto">
                          {images.map((imgUrl, idx) => (
                            <img 
                              key={idx}
                              src={`${BACKEND_URL}${imgUrl}`}
                              alt={`Review photo ${idx + 1}`}
                              className="h-24 w-24 rounded-lg object-cover cursor-pointer hover:opacity-80 hover:scale-105 transition-all border-2 border-gray-200 flex-shrink-0"
                              onClick={() => {
                                setLightboxImages(images.map(url => `${BACKEND_URL}${url}`));
                                setLightboxIndex(idx);
                                setLightboxOpen(true);
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                              title="Click to view full size"
                            />
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          📷 {images.length} image{images.length > 1 ? 's' : ''} attached (click to view)
                        </p>
                      </div>
                    );
                  })()}
                  
                  <p className="text-xs text-gray-500 mt-4">
                    Submitted on {new Date(review.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                    {review.user_id && ' • Registered User'}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Image Lightbox */}
      {lightboxOpen && (
        <ImageLightbox
          images={lightboxImages}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
          onNavigate={(newIndex) => setLightboxIndex(newIndex)}
        />
      )}
    </>
  );
};

export default ReviewsModeration;
